import { useEffect } from 'react';

type StopScannerFn = () => void | boolean;

declare global {
  interface Window {
    __quali_stopScanner?: StopScannerFn;
    __quali_stopScannerAsync?: () => Promise<boolean>;
  }
}

function removeQueryParam(paramName: string) {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(paramName)) return;
    url.searchParams.delete(paramName);
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch {
    // ignore malformed URLs
  }
}

/**
 * Lightweight bridge for native WebView stop-scanner signals.
 * Keeps global hooks available even when BarcodeScanner (Quagga) is not loaded.
 * When the heavy scanner mounts it overwrites `__quali_stopScanner`.
 */
export function useScannerNativeBridge() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStopParams = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const action = params.get('quali_action');
        const stopFlag = params.get('stop_scanner');
        if (action === 'stopScanner' || stopFlag === '1') {
          removeQueryParam(
            action === 'stopScanner' ? 'quali_action' : 'stop_scanner',
          );
          window.__quali_stopScanner?.();
        }
      } catch {
        // ignore
      }
    };

    if (!window.__quali_stopScanner) {
      window.__quali_stopScanner = () => false;
    }
    if (!window.__quali_stopScannerAsync) {
      window.__quali_stopScannerAsync = () =>
        Promise.resolve(Boolean(window.__quali_stopScanner?.()));
    }

    handleStopParams();
    window.addEventListener('popstate', handleStopParams);
    return () => {
      window.removeEventListener('popstate', handleStopParams);
    };
  }, []);
}
